import { apiClient } from './client'

// ==================== TYPES ====================

export interface TerminalType {
  id: string
  name: string
  description: string
  color: string
  icon: string
  isActive: boolean
  terminalCount: number
  createdAt: string
  updatedAt: string
}

export interface RFIDTerminal {
  id: string
  name: string
  serialNumber: string
  ipAddress: string
  type: string
  typeColor: string
  location: string
  institutionId: string
  institutionName: string
  status: 'online' | 'offline' | 'maintenance' | 'error'
  lastPing: string
  firmwareVersion: string
  batteryLevel?: number
  signalStrength: number
  totalScans: number
  dailyScans: number
  description?: string
  createdBy: string
  createdAt: string
  updatedAt: string
}

export interface TerminalStats {
  totalTerminals: number
  onlineTerminals: number
  offlineTerminals: number
  maintenanceTerminals: number
  errorTerminals: number
  byType: Record<string, number>
  byInstitution: Record<string, number>
  totalScansToday: number
}

export interface CreateTerminalTypeRequest {
  name: string
  description: string
  color: string
  icon: string
}

export interface CreateTerminalRequest {
  name: string
  serialNumber: string
  ipAddress: string
  type: string
  location: string
  description?: string
  institutionId?: string // For super admin
}

export interface UpdateTerminalTypeRequest {
  name?: string
  description?: string
  color?: string
  icon?: string
  isActive?: boolean
}

export interface UpdateTerminalRequest {
  name?: string
  location?: string
  description?: string
  status?: 'online' | 'offline' | 'maintenance' | 'error'
  firmwareVersion?: string
  batteryLevel?: number
  signalStrength?: number
  type?: string
}

export interface GetTerminalsParams {
  status?: string
  type?: string
  search?: string
  institutionId?: string // For super admin filtering
}

// ==================== TERMINAL TYPES API ====================

// Get all terminal types
export const getTerminalTypes = async (): Promise<{
  success: boolean
  data: TerminalType[]
  message: string
}> => {
  const response = await apiClient.get('/rfid/types')
  return response.data
}

// Get single terminal type by ID
export const getTerminalTypeById = async (typeId: string): Promise<{
  success: boolean
  data: TerminalType
  message: string
}> => {
  const response = await apiClient.get(`/rfid/types/${typeId}`)
  return response.data
}

// Create new terminal type
export const createTerminalType = async (typeData: CreateTerminalTypeRequest): Promise<{
  success: boolean
  data: TerminalType
  message: string
}> => {
  const response = await apiClient.post('/rfid/types', typeData)
  return response.data
}

// Update terminal type
export const updateTerminalType = async (typeId: string, typeData: UpdateTerminalTypeRequest): Promise<{
  success: boolean
  data: TerminalType
  message: string
}> => {
  const response = await apiClient.put(`/rfid/types/${typeId}`, typeData)
  return response.data
}

// Delete terminal type
export const deleteTerminalType = async (typeId: string): Promise<{
  success: boolean
  data: { id: string; name: string }
  message: string
}> => {
  const response = await apiClient.delete(`/rfid/types/${typeId}`)
  return response.data
}

// Get terminal type statistics
export const getTerminalTypeStats = async (): Promise<{
  success: boolean
  data: {
    totalTypes: number
    activeTypes: number
    inactiveTypes: number
    usageByType: Record<string, number>
  }
  message: string
}> => {
  const response = await apiClient.get('/rfid/types/stats')
  return response.data
}

// ==================== TERMINALS API ====================

// Get all terminals with optional filtering
export const getTerminals = async (params: GetTerminalsParams = {}): Promise<{
  success: boolean
  data: RFIDTerminal[]
  message: string
}> => {
  const searchParams = new URLSearchParams()
  
  if (params.status) searchParams.append('status', params.status)
  if (params.type) searchParams.append('type', params.type)
  if (params.search) searchParams.append('search', params.search)
  if (params.institutionId) searchParams.append('institutionId', params.institutionId)
  
  const queryString = searchParams.toString()
  const url = queryString ? `/rfid/terminals?${queryString}` : '/rfid/terminals'
  
  const response = await apiClient.get(url)
  return response.data
}

// Get single terminal by ID
export const getTerminalById = async (terminalId: string): Promise<{
  success: boolean
  data: RFIDTerminal
  message: string
}> => {
  const response = await apiClient.get(`/rfid/terminals/${terminalId}`)
  return response.data
}

// Create new terminal
export const createTerminal = async (terminalData: CreateTerminalRequest): Promise<{
  success: boolean
  data: RFIDTerminal
  message: string
}> => {
  const response = await apiClient.post('/rfid/terminals', terminalData)
  return response.data
}

// Update terminal
export const updateTerminal = async (terminalId: string, terminalData: UpdateTerminalRequest): Promise<{
  success: boolean
  data: RFIDTerminal
  message: string
}> => {
  const response = await apiClient.put(`/rfid/terminals/${terminalId}`, terminalData)
  return response.data
}

// Delete terminal
export const deleteTerminal = async (terminalId: string): Promise<{
  success: boolean
  data: { id: string; name: string }
  message: string
}> => {
  const response = await apiClient.delete(`/rfid/terminals/${terminalId}`)
  return response.data
}

// Restart terminal
export const restartTerminal = async (terminalId: string): Promise<{
  success: boolean
  data: { id: string; name: string; status: string }
  message: string
}> => {
  const response = await apiClient.post(`/rfid/terminals/${terminalId}/restart`)
  return response.data
}

// Get terminal statistics
export const getTerminalStats = async (): Promise<{
  success: boolean
  data: TerminalStats
  message: string
}> => {
  const response = await apiClient.get('/rfid/terminals/stats')
  return response.data
}

// Update terminal ping (typically called by terminal devices)
export const updateTerminalPing = async (serialNumber: string, pingData: {
  status?: string
  batteryLevel?: number
  signalStrength?: number
  scansCount?: number
}): Promise<{
  success: boolean
  data: { id: string; status: string; lastPing: string }
  message: string
}> => {
  const response = await apiClient.post(`/rfid/ping/${serialNumber}`, pingData)
  return response.data
}

// ==================== RFID CARDS API ====================

export interface RFIDCard {
  id: string
  cardNumber: string
  rfidTag: string
  assignedToId?: string
  assignedToName?: string
  assignedToType: 'student' | 'teacher' | 'parent' | 'driver' | 'admin' | 'visitor'
  cardType: 'wristband' | 'card' | 'keychain' | 'sticker'
  status: 'active' | 'inactive' | 'lost' | 'damaged' | 'suspended'
  institutionId: string
  institutionName: string
  issueDate: string
  expiryDate?: string
  lastUsed?: string
  totalScans: number
  dailyScans: number
  batteryLevel?: number
  notes?: string
  createdBy: string
  createdAt: string
  updatedAt: string
}

export interface RFIDCardStats {
  totalCards: number
  activeCards: number
  inactiveCards: number
  assignedCards: number
  unassignedCards: number
  lostCards: number
  damagedCards: number
  suspendedCards: number
  byType: Record<string, number>
  byStatus: Record<string, number>
  byAssignedType: Record<string, number>
  byInstitution: Record<string, number>
  totalScansToday: number
}

export interface CreateRFIDCardRequest {
  cardNumber: string
  rfidTag: string
  assignedToId?: string
  assignedToType: 'student' | 'teacher' | 'parent' | 'driver' | 'admin' | 'visitor'
  cardType: 'wristband' | 'card' | 'keychain' | 'sticker'
  expiryDate?: string
  notes?: string
  institutionId?: string // For super admin
}

export interface UpdateRFIDCardRequest {
  assignedToId?: string
  assignedToType?: 'student' | 'teacher' | 'parent' | 'driver' | 'admin' | 'visitor'
  cardType?: 'wristband' | 'card' | 'keychain' | 'sticker'
  status?: 'active' | 'inactive' | 'lost' | 'damaged' | 'suspended'
  expiryDate?: string
  batteryLevel?: number
  notes?: string
}

export interface GetRFIDCardsParams {
  status?: string
  cardType?: string
  assignedToType?: string
  assigned?: 'true' | 'false'
  search?: string
  institutionId?: string // For super admin filtering
}

export interface AssignRFIDCardRequest {
  assignedToId: string
  assignedToType: 'student' | 'teacher' | 'parent' | 'driver' | 'admin' | 'visitor'
}

// Get all RFID cards with optional filtering
export const getRFIDCards = async (params: GetRFIDCardsParams = {}): Promise<{
  success: boolean
  data: RFIDCard[]
  message: string
}> => {
  const searchParams = new URLSearchParams()
  
  if (params.status) searchParams.append('status', params.status)
  if (params.cardType) searchParams.append('cardType', params.cardType)
  if (params.assignedToType) searchParams.append('assignedToType', params.assignedToType)
  if (params.assigned) searchParams.append('assigned', params.assigned)
  if (params.search) searchParams.append('search', params.search)
  if (params.institutionId) searchParams.append('institutionId', params.institutionId)
  
  const queryString = searchParams.toString()
  const url = queryString ? `/rfid/cards?${queryString}` : '/rfid/cards'
  
  const response = await apiClient.get(url)
  return response.data
}

// Get single RFID card by ID
export const getRFIDCardById = async (cardId: string): Promise<{
  success: boolean
  data: RFIDCard
  message: string
}> => {
  const response = await apiClient.get(`/rfid/cards/${cardId}`)
  return response.data
}

// Create new RFID card
export const createRFIDCard = async (cardData: CreateRFIDCardRequest): Promise<{
  success: boolean
  data: RFIDCard
  message: string
}> => {
  const response = await apiClient.post('/rfid/cards', cardData)
  return response.data
}

// Update RFID card
export const updateRFIDCard = async (cardId: string, cardData: UpdateRFIDCardRequest): Promise<{
  success: boolean
  data: RFIDCard
  message: string
}> => {
  const response = await apiClient.put(`/rfid/cards/${cardId}`, cardData)
  return response.data
}

// Delete RFID card
export const deleteRFIDCard = async (cardId: string): Promise<{
  success: boolean
  data: { id: string; cardNumber: string }
  message: string
}> => {
  const response = await apiClient.delete(`/rfid/cards/${cardId}`)
  return response.data
}

// Assign RFID card to user
export const assignRFIDCard = async (cardId: string, assignData: AssignRFIDCardRequest): Promise<{
  success: boolean
  data: { id: string; cardNumber: string; assignedToName: string; status: string }
  message: string
}> => {
  const response = await apiClient.post(`/rfid/cards/${cardId}/assign`, assignData)
  return response.data
}

// Unassign RFID card from user
export const unassignRFIDCard = async (cardId: string): Promise<{
  success: boolean
  data: { id: string; cardNumber: string; status: string }
  message: string
}> => {
  const response = await apiClient.post(`/rfid/cards/${cardId}/unassign`)
  return response.data
}

// Get RFID card statistics
export const getRFIDCardStats = async (): Promise<{
  success: boolean
  data: RFIDCardStats
  message: string
}> => {
  const response = await apiClient.get('/rfid/cards/stats')
  return response.data
}